import React, { useState, useEffect } from 'react'
import Card from '../../components/Card.jsx'
import * as api from '../../services/api.js'
import { COMPANIONS, COMPANION_ORDER, STEP_MILESTONES } from './companionData.js'

const TOTAL_ACHIEVEMENTS = COMPANION_ORDER.length * 9 // 8 companions × 9 prizes

export default function AdventureVault() {
  const [data, setData] = useState({ companion: 'carl', goal: 5000, logs: {} })
  useEffect(() => { api.getWalkState('walking').then(d => setData({ companion: 'carl', goal: 5000, logs: {}, ...(d || {}) })) }, [])
  const [filter, setFilter] = useState('all') // 'all' | companion id
  const [expanded, setExpanded] = useState(new Set())
  const [showCollection, setShowCollection] = useState(false)

  function toggleCard(key) {
    setExpanded(prev => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  // Compute per-companion totals and which prizes are unlocked
  function getCompanionStats(id) {
    const log = data.logs?.[id] ?? []
    const totalSteps = log.reduce((s, e) => s + e.steps, 0)
    const unlockedCount = STEP_MILESTONES.filter(m => totalSteps >= m).length
    const explored = totalSteps > 0
    return { totalSteps, unlockedCount, explored }
  }

  const allStats = Object.fromEntries(COMPANION_ORDER.map(id => [id, getCompanionStats(id)]))
  const totalUnlocked = COMPANION_ORDER.reduce((sum, id) => sum + allStats[id].unlockedCount, 0)
  const exploredCount = COMPANION_ORDER.filter(id => allStats[id].explored).length

  // Build flat list of achievements to display
  const displayedItems = COMPANION_ORDER
    .filter(id => filter === 'all' || filter === id)
    .flatMap(id => {
      const companion = COMPANIONS[id]
      const { totalSteps } = allStats[id]
      return companion.prizes.map((prize, i) => ({
        companionId: id,
        companion,
        prize,
        milestone: STEP_MILESTONES[i],
        unlocked: totalSteps >= STEP_MILESTONES[i],
      }))
    })

  const unlockedItems = displayedItems.filter(i => i.unlocked)
  const lockedItems = displayedItems.filter(i => !i.unlocked)

  return (
    <Card label="Check-in · Activities" title="Adventure Vault">

      {/* ── Global summary ── */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        {[
          { value: totalUnlocked, total: TOTAL_ACHIEVEMENTS, label: 'items collected', emoji: '✨' },
          { value: exploredCount, total: 8, label: 'realms explored', emoji: '🗺️' },
        ].map(s => (
          <div key={s.label} className="mission-stat-box" style={{ flex: 1, minWidth: '120px' }}>
            <div style={{ fontSize: '1.4rem' }}>{s.emoji}</div>
            <div className="mission-stat-value">{s.value}<span style={{ fontSize: '0.9rem', fontWeight: 400, color: 'var(--stone)' }}>/{s.total}</span></div>
            <div className="mission-stat-label">{s.label}</div>
          </div>
        ))}
        {/* Overall progress bar */}
        <div style={{ flex: '100%', marginTop: '0.25rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
            <span style={{ fontSize: '0.7rem', color: 'var(--stone)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Overall collection</span>
            <span style={{ fontSize: '0.78rem', color: 'var(--bark)' }}>{Math.round((totalUnlocked / TOTAL_ACHIEVEMENTS) * 100)}%</span>
          </div>
          <div className="walk-goal-bar-wrap" style={{ height: '8px' }}>
            <div className="walk-goal-bar-fill" style={{ width: `${(totalUnlocked / TOTAL_ACHIEVEMENTS) * 100}%` }} />
          </div>
        </div>
      </div>

      {/* ── Realm map ── */}
      <p style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--stone)', marginBottom: '0.6rem' }}>
        Realms
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.4rem', marginBottom: '1.5rem' }}>
        {COMPANION_ORDER.map(id => {
          const c = COMPANIONS[id]
          const { unlockedCount, explored } = allStats[id]
          const isFilter = filter === id
          return (
            <button
              key={id}
              onClick={() => setFilter(isFilter ? 'all' : id)}
              style={{
                background: explored ? c.realmBg : 'var(--surf)',
                border: `2px solid ${isFilter ? c.selectedBorder : 'transparent'}`,
                borderRadius: '12px', padding: '0.7rem 0.4rem', cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
                transition: 'all 0.2s', position: 'relative', overflow: 'hidden',
                boxShadow: isFilter ? `0 0 0 1px ${c.selectedBorder}` : 'none',
                opacity: explored ? 1 : 0.5,
              }}
            >
              {/* Fog overlay for unexplored realms */}
              {!explored && (
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: '1rem', opacity: 0.5 }}>🌫️</span>
                </div>
              )}
              <span style={{ fontSize: '1.5rem', lineHeight: 1 }}>{c.cardEmoji}</span>
              <span style={{ fontSize: '0.6rem', fontWeight: 600, color: explored ? 'white' : 'var(--bark)', textAlign: 'center', fontFamily: 'inherit', lineHeight: 1.2 }}>
                {c.name}
              </span>
              {/* Mini progress pips */}
              <div style={{ display: 'flex', gap: '2px', flexWrap: 'wrap', justifyContent: 'center', maxWidth: '54px' }}>
                {Array.from({ length: 9 }, (_, i) => (
                  <span key={i} style={{
                    width: 5, height: 5, borderRadius: '50%',
                    background: i < unlockedCount
                      ? (explored ? c.selectedBorder : 'var(--accent)')
                      : 'rgba(255,255,255,0.15)',
                    display: 'inline-block', transition: 'background 0.3s',
                  }} />
                ))}
              </div>
              <span style={{ fontSize: '0.52rem', color: explored ? 'rgba(255,255,255,0.5)' : 'var(--stone)' }}>
                {unlockedCount}/9
              </span>
            </button>
          )
        })}
      </div>

      {/* ── Collection toggle ── */}
      <button
        className="ghost-btn"
        onClick={() => setShowCollection(p => !p)}
        style={{ fontSize: '0.82rem', marginTop: '0.25rem', marginBottom: showCollection ? '0.75rem' : 0 }}
      >
        {showCollection ? '▲ Hide collection' : `▼ Collected (${totalUnlocked})`}
      </button>

      {showCollection && (
        <>
          {/* Realm filter label */}
          {filter !== 'all' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <span style={{ fontSize: '0.82rem', color: 'var(--bark)' }}>Showing {COMPANIONS[filter].name}</span>
              <button onClick={() => setFilter('all')} style={{ fontSize: '0.72rem', color: 'var(--stone)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                show all
              </button>
            </div>
          )}

          {/* Unlocked items */}
          {unlockedItems.length > 0 && (
            <>
              <p style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--stone)', marginBottom: '0.6rem' }}>
                ✨ Collected ({unlockedItems.length})
              </p>
              <div className="vault-grid" style={{ marginBottom: '1.25rem' }}>
                {unlockedItems.map(({ companionId, companion, prize, milestone }, idx) => {
                  const key = `${companionId}-${idx}`
                  const isOpen = expanded.has(key)
                  return (
                    <div
                      key={key}
                      className="vault-card vault-card--unlocked"
                      style={{ background: companion.cardBg, borderColor: companion.selectedBorder, cursor: 'pointer' }}
                      onClick={() => toggleCard(key)}
                    >
                      <div className="vault-card-realm">
                        <span>{companion.cardEmoji}</span>
                        <span>{companion.name}</span>
                        <span style={{ marginLeft: 'auto', fontSize: '0.6rem', color: 'rgba(255,255,255,0.4)', transition: 'transform 0.2s', display: 'inline-block', transform: isOpen ? 'rotate(180deg)' : 'none' }}>▼</span>
                      </div>
                      <div className="vault-card-emoji">{prize.emoji}</div>
                      <div className="vault-card-name">{prize.name}</div>
                      {isOpen && (
                        <>
                          <div className="vault-card-desc">{prize.desc}</div>
                          <div className="vault-card-steps">{milestone.toLocaleString()} steps</div>
                        </>
                      )}
                    </div>
                  )
                })}
              </div>
            </>
          )}

          {/* Locked items */}
          {lockedItems.length > 0 && (
            <>
              <p style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--stone)', marginBottom: '0.6rem' }}>
                🔒 Still to discover ({lockedItems.length})
              </p>
              <div className="vault-grid">
                {lockedItems.map(({ companionId, companion, milestone }, idx) => (
                  <div
                    key={`${companionId}-locked-${idx}`}
                    className="vault-card vault-card--locked"
                    style={{ background: companion.cardBg }}
                  >
                    <div className="vault-card-realm" style={{ opacity: 0.5 }}>
                      <span>{companion.cardEmoji}</span>
                      <span>{companion.name}</span>
                    </div>
                    <div className="vault-card-emoji" style={{ filter: 'grayscale(100%)', opacity: 0.2 }}>🔒</div>
                    <div className="vault-card-name" style={{ opacity: 0.35 }}>???</div>
                    <div className="vault-card-steps" style={{ opacity: 0.4 }}>{milestone.toLocaleString()} steps</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {totalUnlocked === 0 && (
            <p style={{ fontSize: '0.88rem', color: 'var(--stone)', fontStyle: 'italic', textAlign: 'center', padding: '1.5rem 0' }}>
              No achievements yet — log a walk in the Walking Challenge above to start collecting.
            </p>
          )}

          {totalUnlocked === TOTAL_ACHIEVEMENTS && (
            <div style={{ textAlign: 'center', padding: '1rem 0', marginTop: '0.5rem' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.4rem' }}>🏆</div>
              <p style={{ fontSize: '0.95rem', color: 'var(--bark)', fontWeight: 500 }}>All 72 items collected.</p>
              <p style={{ fontSize: '0.82rem', color: 'var(--stone)', fontStyle: 'italic' }}>
                Every realm explored. Every companion satisfied. That is a lot of walking.
              </p>
            </div>
          )}
        </>
      )}

    </Card>
  )
}
